import { useState, useRef, useCallback } from "react";
import "./PhoneInputWithNumpad.css";

const BLUR_DELAY_MS = 200;

function formatPhone(digits) {
  if (!digits) return "";
  const d = digits.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function PhoneInputWithNumpad({
  value = "",
  onChange,
  onBlur,
  onComplete,
  showNumpad: enableNumpad = false,
  numpadInline = false,
  placeholder = "(512) 555-0123",
  disabled = false,
  className = "",
  inputRef: forwardedRef,
  onKeyDown,
  inputClassName = "",
}) {
  const [numpadVisible, setNumpadVisible] = useState(false);
  const blurTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const digits = (value || "").replace(/\D/g, "");
  const displayValue = formatPhone(digits);

  const updateValue = useCallback(
    (newDigits) => {
      const formatted = formatPhone(newDigits);
      onChange?.(formatted);
    },
    [onChange]
  );

  const triggerComplete = useCallback(
    (formattedValue) => {
      if (formattedValue.replace(/\D/g, "").length === 10) {
        onComplete?.(formattedValue);
      }
    },
    [onComplete]
  );

  const handleDigitClick = (digit) => {
    if (digits.length >= 10) return;
    const newDigits = digits + digit;
    updateValue(newDigits);
    if (digits.length === 9) {
      const formatted = formatPhone(newDigits);
      setTimeout(() => {
        inputRef.current?.blur();
        triggerComplete(formatted);
      }, 0);
    }
  };

  const handleBackspace = () => {
    if (digits.length === 0) return;
    updateValue(digits.slice(0, -1));
  };

  const handleClear = () => {
    updateValue("");
  };

  const handleFocus = () => {
    if (!enableNumpad) return;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setNumpadVisible(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setNumpadVisible(false);
      blurTimeoutRef.current = null;
    }, BLUR_DELAY_MS);
  };

  const handleNumpadMouseDown = (e) => {
    e.preventDefault();
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onKeyDown?.(e);
    }
  };

  return (
    <div ref={containerRef} className={`phone-input-with-numpad ${className}`}>
      <input
        ref={(el) => {
          inputRef.current = el;
          if (forwardedRef) {
            if (typeof forwardedRef === "function") forwardedRef(el);
            else forwardedRef.current = el;
          }
        }}
        type="tel"
        inputMode="tel"
        autoComplete="new-password"
        className={`form-control form-control-lg phone-numpad-input ${inputClassName}`}
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => {
          const newDigits = e.target.value.replace(/\D/g, "");
          updateValue(newDigits);
          if (newDigits.length >= 10) {
            const formatted = formatPhone(newDigits);
            e.target.blur();
            triggerComplete(formatted);
          }
        }}
        onFocus={handleFocus}
        onBlur={(e) => {
          handleBlur();
          onBlur?.(e);
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Phone number"
      />
      {enableNumpad && numpadVisible && !disabled && (
        <div
          className={`phone-numpad-panel${numpadInline ? " phone-numpad-panel--inline" : ""}`}
          onMouseDown={handleNumpadMouseDown}
          onTouchStart={handleNumpadMouseDown}
        >
          <div className="phone-numpad-grid">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button
                key={d}
                type="button"
                className="phone-numpad-btn phone-numpad-digit"
                onClick={() => handleDigitClick(d)}
                disabled={digits.length >= 10}
                aria-label={`Digit ${d}`}
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              className="phone-numpad-btn phone-numpad-clear"
              onClick={handleClear}
              disabled={digits.length === 0}
              aria-label="Clear"
            >
              CLR
            </button>
            <button
              type="button"
              className="phone-numpad-btn phone-numpad-digit"
              onClick={() => handleDigitClick("0")}
              disabled={digits.length >= 10}
              aria-label="Digit 0"
            >
              0
            </button>
            <button
              type="button"
              className="phone-numpad-btn phone-numpad-backspace"
              onClick={handleBackspace}
              disabled={digits.length === 0}
              aria-label="Backspace"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
